// @ts-nocheck
import clsx from 'clsx';
import React, { useState, forwardRef, useMemo, useRef } from 'react';

import Item from './components/item';
import * as styles from './select.styles';
import { useSelectListener, setRef } from '../../../hooks';
import { Dropdown, Icon, Scrollable } from '../../atoms';
import { Control } from '../control';
import { useFieldContext } from '../field/context';
import { Input } from '../input';

/** @type {typeof import(".").Select} */
const Select = forwardRef(({ className, placeholder, position, gapless = false, onBlur, onFocus, children, ...props }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef(null);
  const inputRef = useRef(null);
  const [focusItem, setFocusItem] = useSelectListener({
    enabled: isOpen,
    ref: contentRef,
    selector: '[data-dropdown-item]',
  });

  const { disabled, onChange, name, value, ...fieldProps } = useFieldContext(props);

  const parsedChildren = useMemo(() => {
    return React.Children.toArray(children)
      .filter(React.isValidElement)
      .map((child, i) => {
        const isSelected = value !== undefined && child.props.value === value;
        const hover = i === focusItem || isSelected;
        return React.cloneElement(child, {
          className: clsx(
            {
              [styles.variants.hover]: hover,
            },
            child.props.className,
          ),
          selected: child.props.selected || isSelected,
          'data-dropdown-item': true,
          onClick: (evt) => {
            child.props?.onChange?.(evt);
            evt.target.name = name;
            evt.target.value = child.props.value;
            child.props.onClick?.(evt);
            onChange?.(evt);
            setIsOpen(false);
          },
        });
      });
  }, [children, focusItem, name, onChange, value]);

  const handleSearch = (evt) => {
    if (!isOpen) {
      return;
    }
    if (['Tab', 'Escape'].includes(evt.code)) {
      setIsOpen(false);
      return;
    }

    if (['Enter', 'NumpadEnter'].includes(evt.code)) {
      setTimeout(() => {
        setIsOpen(false);
      }, 20);
      if (parsedChildren[focusItem]) {
        const child = parsedChildren[focusItem];
        evt.target.name = name;
        evt.target.value = child.props.value;
        onChange?.(evt);
      }
    }
    if (evt.key.length === 1) {
      evt.stopPropagation();
      evt.preventDefault();
      const searchIndex = parsedChildren.findIndex((child) => {
        const text = typeof child.props.children === 'string' ? child.props.children : child.props.text || '';
        return text.toLowerCase?.().startsWith(evt.key);
      });
      setFocusItem((current) => {
        return searchIndex >= 0 ? searchIndex : current;
      });
    }
  };

  // Display selected child if value is set or placeholder
  const displayValue =
    parsedChildren.find((child) => {
      return child.props.value !== undefined && child.props.value === value;
    })?.props?.children ?? placeholder;

  return (
    <Dropdown
      gapless={gapless}
      position={position}
      className={clsx(styles.select, className, {
        [styles.variants.open]: isOpen,
        [styles.variants.disabled]: disabled,
      })}
      onOpen={() => {
        if (disabled) {
          return;
        }
        setIsOpen(!isOpen);
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      open={isOpen}
    >
      <Dropdown.Trigger>
        <Control className={styles.components.control} tabIndex={-1} role="combobox">
          <Input
            as="div"
            onKeyDown={handleSearch}
            onBlur={(evt) => {
              evt.target.name = name;
              onBlur?.(evt);
            }}
            onFocus={(evt) => {
              evt.target.name = name;
              onFocus?.(evt);
            }}
            ref={setRef(inputRef, ref)}
            disabled={disabled}
            tabIndex={0}
            className={clsx(styles.components.trigger, {
              [styles.components.placeholder]: placeholder === displayValue,
              [styles.variants.open]: isOpen,
              [styles.variants.disabled]: disabled,
            })}
            name={name}
            {...fieldProps}
            {...props}
          >
            {displayValue}
          </Input>
          <Icon
            className={clsx(styles.components.icon, {
              [styles.variants.disabled]: disabled,
            })}
            invariant
            disabled={disabled}
            name="back"
          />
        </Control>
      </Dropdown.Trigger>
      <Dropdown.Content as={Scrollable} direction="vertical" className={styles.components.content} fullWidth>
        <div ref={contentRef}>{parsedChildren}</div>
      </Dropdown.Content>
    </Dropdown>
  );
});

Select.Item = Item;

if (process.env.NODE_ENV !== 'production') {
  Select.displayName = 'Select';
}

export default Select;
